import { DateTime } from 'luxon';
import type { ActionTree } from 'vuex';
import type { IComment } from '@lib-shared/chat/types/comment';
import { translate } from '@lib-shared/helpers/translation.helper';
import { EmojiHelper } from '@lib-shared/emoji/helpers/emoji.helper';
import type { IChatState } from '@signing-page/chat/store/chat.types';
import { EChatAction, EChatMutation } from '@signing-page/chat/store/chat.constants';
import { ChatService } from '@signing-page/api/chat/services/chat.service';

import type { IRootState } from '@signing-page/store/store';
import { MergeTagHelper } from '@signing-page/helpers/merge-tag.helper';
import type { IRecipient } from '@signing-page/documents/types/recipient';
import { alertNamespace, EAlertAction } from '@signing-page/alert/store/alert.constants';

export const actions: ActionTree<IChatState, IRootState> = {
  async [EChatAction.LoadChat]({ commit, dispatch, rootState }) {
    commit(EChatMutation.SetLoading, true);
    try {
      const comments: IComment[] = await ChatService.getComments();
      const { document } = rootState.document;
      const { entity } = rootState.entity;
      const currentRecipient: IRecipient = document.recipients.find(
        (recipient: IRecipient) => recipient.id === rootState.recipientId
      );

      const parsedComments: IComment[] = comments.map((comment: IComment) => ({
        ...comment,
        commentText: MergeTagHelper.replaceTags(comment.commentText, {
          recipient: currentRecipient,
          document,
          entity,
        }),
      }));
      dispatch(EChatAction.LoadChatSuccess, parsedComments);
    } catch (error) {
      commit(EChatMutation.SetLoading, false);
      dispatch(EChatAction.LoadChatFailed);
    }
  },
  async [EChatAction.LoadChatSuccess]({ commit, dispatch, state }, comments: IComment[]) {
    if (!state.commentsRead) {
      commit(EChatMutation.SetCommentsRead, comments);
    }
    dispatch(EChatAction.SetChatMessages, comments);
    commit(EChatMutation.SetLoading, false);
  },
  async [EChatAction.LoadChatFailed]({ commit, dispatch }) {
    commit(EChatMutation.SetLoading, false);
    dispatch(
      `${alertNamespace}/${EAlertAction.ShowDanger}`,
      translate('failed-to-load-chat-messages'),
      {
        root: true,
      }
    );
  },
  async [EChatAction.AddComment]({ dispatch, state, rootState }, commentText: string) {
    const { recipientId, authId, documentId }: IRootState = rootState;
    const { comments } = state;
    const comment: Partial<IComment> = {
      recipientId,
      commentText,
      createdAt: DateTime.utc().toISO(),
    };
    try {
      dispatch(EChatAction.SetChatMessages, [...comments, comment]);
      const success: boolean = await ChatService.addComment(
        documentId,
        recipientId,
        authId,
        commentText
      );
      if (!success) {
        dispatch(EChatAction.SetChatMessages, comments);
      }
    } catch (e) {
      dispatch(EChatAction.SetChatMessages, comments);
    }
  },
  [EChatAction.SetIsOpen]({ commit, dispatch }, isOpen: boolean) {
    if (isOpen) {
      dispatch(EChatAction.ReadComments);
    }
    commit(EChatMutation.SetIsOpen, isOpen);
  },
  [EChatAction.ReadComments]({ commit, state }) {
    const notificationDotDelay = 2500;
    setTimeout(() => {
      commit(EChatMutation.SetCommentsRead, state.comments);
    }, notificationDotDelay);
  },
  async [EChatAction.AutoComment]({ rootState, state, dispatch }) {
    const { comments }: IChatState = state;
    if (comments.length) {
      return;
    }
    const { documentId, recipientId, authId }: IRootState = rootState;

    try {
      const newComment: boolean = await ChatService.autoComment(documentId, recipientId, authId);
      if (newComment) {
        dispatch(EChatAction.LoadChat);
      }
    } catch (e) {
      console.error(e);
    }
  },
  [EChatAction.SetSystemMessageOpen]({ commit }, open: boolean) {
    commit(EChatMutation.SetSystemMessageOpen, open);
  },
  [EChatAction.SetChatMessages]({ commit }, comments: IComment[]) {
    const parsedComments: IComment[] = [];
    comments.forEach((comment: IComment) => {
      const parsedCommentText: string = EmojiHelper.decode(comment.commentText);
      parsedComments.push({ ...comment, commentText: parsedCommentText });
    });
    commit(EChatMutation.SetChatMessages, parsedComments);
    commit(EChatMutation.SetLoading, false);
  },
};
